import { InvoiceModel, useRestApiProvider } from "@jugl-web/rest-api";
import { useCallback } from "react";
import writeXlsxFile, { SheetData } from "write-excel-file";
import { priceDisplay, useTranslations } from "@jugl-web/utils";
import format from "date-fns/format";
import { useInvoiceTypeToLabelDictionary } from "@jugl-web/rest-api/invoices/hooks/useInvoiceTypeToLabelDictionary";
import { getCustomerDetails } from "@jugl-web/domain-resources/customers/utils";
import { useInvoicesTableContext } from "../../../context/useInvoicesTableContext";

export const useExportInvoices = () => {
  const { t } = useTranslations();
  const { entity, isGuest } = useInvoicesTableContext();
  const { customersApi } = useRestApiProvider();
  const [getCustomer] = customersApi.useLazyCustomerQuery();
  const dict = useInvoiceTypeToLabelDictionary();

  const generateHeaderRow = useCallback(
    () => [
      {
        value: t({
          id: "invoices.export.invoice-number",
          defaultMessage: "Invoice Number",
        }),
        fontWeight: "bold" as const,
        backgroundColor: "#f5f5f5",
      },
      {
        value: t({
          id: "invoices.export.reference-number",
          defaultMessage: "Reference Number",
        }),
        fontWeight: "bold" as const,
        backgroundColor: "#f5f5f5",
      },
      {
        value: t({
          id: "invoices.export.type",
          defaultMessage: "Type",
        }),
        fontWeight: "bold" as const,
        backgroundColor: "#f5f5f5",
      },
      {
        value: t({
          id: "invoices.export.issue-date",
          defaultMessage: "Issue Date",
        }),
        fontWeight: "bold" as const,
        backgroundColor: "#f5f5f5",
      },
      {
        value: t({
          id: "invoices.export.due-date",
          defaultMessage: "Due Date",
        }),
        fontWeight: "bold" as const,
        backgroundColor: "#f5f5f5",
      },
      {
        value: t({
          id: "invoices.export.customer",
          defaultMessage: "Customer",
        }),
        fontWeight: "bold" as const,
        backgroundColor: "#f5f5f5",
      },
      {
        value: t({
          id: "invoices.export.payment-status",
          defaultMessage: "Payment Status",
        }),
        fontWeight: "bold" as const,
        backgroundColor: "#f5f5f5",
      },
      {
        value: t({
          id: "invoices.export.total-value",
          defaultMessage: "Total Value",
        }),
        fontWeight: "bold" as const,
        backgroundColor: "#f5f5f5",
      },
      {
        value: t({
          id: "invoices.export.share-link",
          defaultMessage: "Share Link",
        }),
        fontWeight: "bold" as const,
        backgroundColor: "#f5f5f5",
      },
    ],
    [t]
  );

  const generateInvoiceRow = useCallback(
    (invoice: InvoiceModel) => {
      const getInvoiceStatus = (status: string) => {
        const capitalizedStatus =
          status.charAt(0).toUpperCase() + status.slice(1);
        return t({
          defaultMessage: capitalizedStatus,
          id: `invoices.status.${status}`,
        });
      };

      const totalValue = priceDisplay(
        invoice.grand_total * 100,
        invoice.currency,
        false,
        {
          isString: true,
        }
      ) as string;

      return [
        { value: invoice.no },
        { value: invoice.ref_num || "-" },
        { value: dict[invoice.type] },
        { value: format(new Date(invoice.date), "dd MMM yyyy") },
        { value: format(new Date(invoice.due_on), "dd MMM yyyy") },
        { value: "-" },
        { value: getInvoiceStatus(invoice.status) },
        { value: totalValue },
        { value: invoice.short_url },
      ];
    },
    [t, dict]
  );

  const generateExcel = useCallback(
    async (invoices: InvoiceModel[]) => {
      if (!invoices.length) {
        throw new Error("No invoices to export");
      }

      const processedData: SheetData = [];

      processedData.push(generateHeaderRow());

      const invoiceRows = invoices.map(generateInvoiceRow);

      const customerPromises = invoices
        .filter((invoice) => invoice.customer_id)
        .map(async (invoice) => {
          try {
            if (!invoice.customer_id) {
              return { id: invoice.customer_id, name: "-" };
            }

            const response = await getCustomer({
              entityId: entity.id,
              customerId: invoice.customer_id,
              isGuest,
              meta: { silent: true },
            }).unwrap();
            const customerDetails = getCustomerDetails(response.data);
            return {
              id: response.data.id,
              name: customerDetails.fullName || "-",
            };
          } catch {
            return { id: invoice.customer_id, name: "-" };
          }
        });

      const customersResponses = await Promise.all(customerPromises);

      invoiceRows.forEach((row, index) => {
        const invoice = invoices[index];
        if (invoice.customer_id) {
          row[5] = {
            value:
              customersResponses.find(
                (customer) => customer.id === invoice.customer_id
              )?.name || "-",
          };
        }
      });

      processedData.push(...invoiceRows);

      const timestamp = format(new Date(), "yyyy-MM-dd-HH-mm");
      const fileName = `invoices-export-${timestamp}.xlsx`;

      await writeXlsxFile(processedData, {
        columns: Array(8).fill({ width: 20 }),
        fileName,
      });
    },
    [generateHeaderRow, generateInvoiceRow, getCustomer, entity.id, isGuest]
  );

  return { generateExcel };
};
